 //Pricing.tsx
import React, { useState, useEffect } from 'react';
import type { TourProps } from 'antd';
import { resolveFrontendApiBase } from './utils/apiBase';
import { Typography, Spin, Button } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { CustomPricing } from './CustomPricing';
import axios from './utils/accountApi';
import { getAuthHeaders } from './utils/authHeaders';
import AppPageHeader from './components/AppPageHeader';
import AppTour, { renderAppTourPanel } from './tours/AppTour';
import { tourTarget, useGuidedTour } from './tours/useGuidedTour';
import ProjectCostEstimator from './components/ProjectCostEstimator';
import './CustomPricing.css';

const { Title } = Typography;

const API_BASE = resolveFrontendApiBase();

interface PricingProps {
  isAuth: boolean;
  usersSharedState: any;
  setUsersSharedState: any;  
  showLoginModal: () => void;
}

const Pricing: React.FC<PricingProps> = ({ isAuth, usersSharedState, setUsersSharedState, showLoginModal }) => {
  const [currentPlanTier, setCurrentPlanTier] = useState(0);
  const [currentPlanPriceId, setCurrentPlanPriceId] = useState<string | undefined>(undefined);
  const [loadingTier, setLoadingTier] = useState<boolean>(true);
  const [currentPlanIsLegacy, setCurrentPlanIsLegacy] = useState<boolean>(false);
  const [pendingChange, setPendingChange] = useState<null | {
    targetPriceId: string;
    targetProductId?: string;
    effectiveAt?: number;
    action?: 'upgrade' | 'downgrade';
  }>(null);
  const [currentPlanForeverDiscountPercent, setCurrentPlanForeverDiscountPercent] = useState<number>(0);
  const pricingTour = useGuidedTour({
    userId: usersSharedState?.userID,
    apiBase: API_BASE,
    flag: 'pricingTourSeen',
    enabled: true,
    autoStart: false,
  });
  const pricingTourSteps = React.useMemo<TourProps['steps']>(() => ([
    {
      title: 'Choose how to buy credits',
      description: renderAppTourPanel('Pricing separates recurring subscription credits from one-time Forever Credits.'),
      target: tourTarget('pricing-header'),
    },
    {
      title: 'Estimate before uploading',
      description: renderAppTourPanel('Enter an approximate word count to see expected credits and a current purchase estimate without uploading a manuscript.'),
      target: tourTarget('pricing-cost-estimator', 'pricing-header'),
    },
    {
      title: 'Compare subscription plans',
      description: renderAppTourPanel('Subscription plans refresh credits on schedule and can be upgraded or changed from here.'),
      target: tourTarget('pricing-subscriptions'),
    },
    {
      title: 'Add Forever Credits',
      description: renderAppTourPanel('Forever Credits are one-time packs that do not expire and can supplement your subscription.'),
      target: tourTarget('pricing-forever-credits', 'pricing-subscriptions'),
    },
    {
      title: 'Continue to checkout',
      description: renderAppTourPanel('Plan buttons open checkout or sign-in prompts depending on your account state.'),
      target: tourTarget('pricing-subscriptions'),
    },
  ]), []);

  const fetchTierAndSchedule = async () => {
    if (!isAuth || !usersSharedState?.userID) return;
    setLoadingTier(true);
    try {
      // Current plan tier
      const creditResponse = await axios.get(
        `${API_BASE}/check_credit_balance/`,
        { params: { userID: usersSharedState.userID } }
      );
      const planProductID = typeof creditResponse.data.planProductID === 'string' ? creditResponse.data.planProductID : undefined;
      const planPriceID = typeof creditResponse.data.planPriceID === 'string' ? creditResponse.data.planPriceID : undefined;
      const fallbackPriceID = typeof creditResponse.data.priceID === 'string' ? creditResponse.data.priceID : undefined;
      const planLookupID =
        planPriceID ||
        fallbackPriceID ||
        (typeof creditResponse.data.planLookupID === 'string' ? creditResponse.data.planLookupID : undefined) ||
        (typeof creditResponse.data.planID === 'string' ? creditResponse.data.planID : undefined) ||
        planProductID;

      if (!planLookupID) {
        setCurrentPlanTier(0);
        setCurrentPlanPriceId(undefined);
        setCurrentPlanIsLegacy(false);
        setCurrentPlanForeverDiscountPercent(0);
        return;
      }

      const planResponse = await axios.get(`${API_BASE}/get_plan_details/${planLookupID}`, {
        params: planPriceID ? { priceId: planPriceID } : undefined,
      });
      setCurrentPlanTier(planResponse.data.tier || 0);
      setCurrentPlanPriceId(planPriceID || fallbackPriceID || planResponse.data?.stripePriceID || undefined);
      setCurrentPlanIsLegacy(Boolean(planResponse.data?.isLegacy));
      const foreverDiscountRaw = planResponse.data?.foreverCreditDiscountPercent;
      const foreverDiscountValue =
        typeof foreverDiscountRaw === 'number'
          ? foreverDiscountRaw
          : Number(foreverDiscountRaw ?? 0);
      setCurrentPlanForeverDiscountPercent(Number.isFinite(foreverDiscountValue) ? foreverDiscountValue : 0);

      // Scheduled change details (if any)
      try {
        const headers = await getAuthHeaders();
        const detailsResp = await axios.get(`${API_BASE}/get-subscription-details`, {
          params: { userID: usersSharedState.userID },
          headers,
        });
        const d = detailsResp.data || {};
        if (d.scheduled_target_price_id) {
          setPendingChange({
            targetPriceId: d.scheduled_target_price_id,
            targetProductId: d.scheduled_target_product_id,
            effectiveAt: d.scheduled_effective_date,
            action: d.scheduled_action,
          });
        } else {
          setPendingChange(null);
        }
      } catch {
        setPendingChange(null);
      }
    } catch (e) {
      console.error('Failed to fetch current plan tier:', e);
      setCurrentPlanTier(0);
      setCurrentPlanPriceId(undefined);
      setPendingChange(null);
      setCurrentPlanIsLegacy(false);
      setCurrentPlanForeverDiscountPercent(0);
    } finally {
      setLoadingTier(false);
    }
  };

  useEffect(() => {
    if (!isAuth || !usersSharedState?.userID) return;
    fetchTierAndSchedule();
  }, [isAuth, usersSharedState?.userID]);



  return (
    <div className="pricing-page-shell">
      <div className="pricing-page-inner">
        <div data-tour-id="pricing-header" data-testid="tutorial:pricing:header">
          <AppPageHeader
            className="pricing-page-header"
            variant="banner"
            title="Pricing"
            titleId="pricing-title"
            description="Choose monthly subscription credits or add Forever Credits when you need more room."
            actions={(
              <Button
                icon={<QuestionCircleOutlined />}
                onClick={pricingTour.start}
                data-tour-id="pricing-tour-trigger"
                className="pricing-tour-link app-tour-trigger"
              >
                Take a tour
              </Button>
            )}
          />
        </div>

        <ProjectCostEstimator />

        {isAuth ? (
          <div>
            {/* Subscription Plans */}
            <section className="pricing-section-surface" data-tour-id="pricing-subscriptions" data-testid="tutorial:pricing:subscriptions">
              <div className="pricing-section-header">
                <Title level={3}>Upgrade Your Plan</Title>
                <p>Subscription credits refresh on schedule and stay protected while you remain subscribed.</p>
              </div>
              {loadingTier ? (
                <div className="pricing-loading-state">
                  <Spin />
                </div>
              ) : (
                <CustomPricing 
                  userID={usersSharedState.userID}
                  email={usersSharedState.email}
                  isForeverCredits={false}
                  currentPlanPriceId={currentPlanPriceId}
                  currentPlanIsLegacy={currentPlanIsLegacy}
                  currentPlanForeverDiscountPercent={currentPlanForeverDiscountPercent}
                  isAuth={isAuth}
                  showLoginModal={showLoginModal}
                  onSuccess={fetchTierAndSchedule}
                  pendingChange={pendingChange || undefined}
                />
              )}
            </section>

            <div className="pricing-page-divider" />

            {/* Forever Credits */}
            <section className="pricing-section-surface" data-tour-id="pricing-forever-credits" data-testid="tutorial:pricing:forever-credits">
              <div className="pricing-section-header">
                <Title level={3}>Purchase Forever Credits A la Carte</Title>
                <p>One-time credit packs never expire and can supplement your subscription.</p>
              </div>
             <CustomPricing 
               userID={usersSharedState.userID}
               email={usersSharedState.email}
               isForeverCredits={true}
               currentPlanPriceId={currentPlanPriceId}
               currentPlanIsLegacy={currentPlanIsLegacy}
               currentPlanForeverDiscountPercent={currentPlanForeverDiscountPercent}
                isAuth={isAuth}
                showLoginModal={showLoginModal}
              />
            </section>
          </div>
        ) : (
          <section className="pricing-section-surface" data-tour-id="pricing-subscriptions" data-testid="tutorial:pricing:subscriptions">
            <div className="pricing-section-header">
              <Title level={3}>Subscription Plans</Title>
              <p>Start with a monthly or annual plan. You can add Forever Credits after signing in.</p>
            </div>
            <CustomPricing
              userID=""
              email=""
              isForeverCredits={false}
              currentPlanPriceId={undefined}
              currentPlanIsLegacy={false}
              currentPlanForeverDiscountPercent={0}
              isAuth={isAuth}
              showLoginModal={showLoginModal}
            />
          </section>
        )}
        <AppTour
          open={pricingTour.open}
          steps={pricingTourSteps}
          current={pricingTour.current}
          onChange={pricingTour.setCurrent}
          onClose={pricingTour.close}
          onFinish={pricingTour.close}
        />
      </div>
    </div>
  );
};

export default Pricing;
